import React, { useEffect, useState } from "react";
import { ImagePlus, Loader2, Package, Sparkles } from "lucide-react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { API_BASE_URL } from "@/lib/apiClient";
import { useLocalization } from "@/lib/localization";

export function ProductArtwork({
  className = "",
  compact = false,
  imageGenerationPhase = "",
  imageClassName = "",
  iconClassName = "",
  isGeneratingImage = false,
  onGenerateImage,
  product,
}) {
  const { messages } = useLocalization();
  const shouldShowImage = Boolean(product?.url) && product?.urlStatus === "completed";
  const [failed, setFailed] = useState(false);
  const imageSrc = shouldShowImage
    ? (String(product.url).startsWith("http://") || String(product.url).startsWith("https://")
        ? product.url
        : `${API_BASE_URL}${product.url}`)
    : "";
  const canGenerate = typeof onGenerateImage === "function";

  useEffect(() => {
    setFailed(false);
  }, [product?.url, product?.urlStatus]);

  const openGenerator = (event) => {
    event.stopPropagation();
    onGenerateImage?.(product);
  };

  if (shouldShowImage && !failed) {
    return (
      <div data-image-generation-phase={imageGenerationPhase || undefined} className={`relative overflow-hidden bg-[var(--product-image-background)] p-1.5 ${className}`}>
        <img
          src={imageSrc}
          alt={product.name}
          className={`h-full w-full bg-[var(--product-image-background)] object-contain ${imageClassName}`}
          loading="lazy"
          onError={() => setFailed(true)}
        />
        {isGeneratingImage ? (
          <div className="absolute inset-0 flex items-center justify-center bg-background/78 backdrop-blur-[2px]">
            {compact ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <ProcessingPill label={messages.common.processingImage} />
            )}
          </div>
        ) : null}
      </div>
    );
  }

  if (compact) {
    const artwork = (
      <button
        type="button"
        data-image-generation-phase={imageGenerationPhase || undefined}
        disabled={isGeneratingImage || !canGenerate}
        onClick={openGenerator}
        aria-label={`${messages.common.generateImage} — ${product?.name || ""}`}
        className={`group relative flex items-center justify-center overflow-hidden border border-primary/10 bg-[linear-gradient(145deg,hsl(var(--primary)/0.11),hsl(var(--primary)/0.025))] text-primary shadow-[inset_0_1px_0_hsl(var(--background)/0.8)] transition-all hover:border-primary/25 hover:bg-primary/10 disabled:cursor-default ${className}`}
      >
        <span className="absolute -right-2 -top-2 h-5 w-5 rounded-full bg-primary/10 blur-sm" />
        {isGeneratingImage ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <Package className={iconClassName || "h-4 w-4 text-primary/70"} strokeWidth={1.6} />
            {canGenerate ? (
              <span className="absolute bottom-0.5 right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background/80 transition-transform group-hover:scale-110">
                <Sparkles className="h-2 w-2" strokeWidth={2.2} />
              </span>
            ) : null}
          </>
        )}
      </button>
    );

    if (!canGenerate) {
      return artwork;
    }

    return (
      <TooltipProvider delayDuration={250}>
        <Tooltip>
          <TooltipTrigger asChild>{artwork}</TooltipTrigger>
          <TooltipContent side="right" className="rounded-lg px-2.5 py-1.5 text-[11px]">
            {isGeneratingImage ? messages.common.processingImage : messages.common.generateImage}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div
      data-image-generation-phase={imageGenerationPhase || undefined}
      className={`relative flex items-center justify-center overflow-hidden border border-primary/10 bg-[radial-gradient(circle_at_28%_18%,hsl(var(--primary)/0.14),transparent_38%),linear-gradient(145deg,hsl(var(--muted)/0.7),hsl(var(--background)))] ${className}`}
    >
      <div className="pointer-events-none absolute -left-8 -top-10 h-28 w-28 rounded-full border border-primary/10" />
      <div className="pointer-events-none absolute -bottom-12 -right-8 h-32 w-32 rounded-full bg-primary/[0.045]" />
      <div className="relative flex max-w-[220px] flex-col items-center px-5 py-6 text-center">
        <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/10 bg-background/90 text-primary shadow-[0_16px_35px_-22px_hsl(var(--primary))]">
          {isGeneratingImage ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <ImagePlus className={iconClassName || "h-5 w-5"} strokeWidth={1.6} />
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-background">
                <Sparkles className="h-2.5 w-2.5" />
              </span>
            </>
          )}
        </div>

        {canGenerate ? (
          <button
            type="button"
            disabled={isGeneratingImage}
            onClick={openGenerator}
            className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-full border border-primary/15 bg-background/90 px-3 text-[10px] font-semibold tracking-[0.04em] text-primary shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:bg-background disabled:translate-y-0 disabled:cursor-wait"
          >
            {isGeneratingImage ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                {messages.common.processingImage}
              </>
            ) : (
              <>
                <Sparkles className="h-3 w-3" />
                {messages.common.generateImage}
              </>
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ProcessingPill({ label }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-background/95 px-3 py-1.5 text-[10px] font-semibold tracking-[0.06em] text-primary shadow-sm">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      {label}
    </div>
  );
}
